/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDetailActions.tsx
import css from '@emotion/css/macro'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import ShareIcon from 'src/components/icons/ShareIcon'
import Button from 'src/components/ts-components/Button'

interface EventDetailActionsProps {
  canEdit: boolean
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
  /** Opens the share modal. Available to ANY viewer — see the guard below. */
  onShare: () => void
}

/**
 * Edit / delete controls for the event detail header.
 *
 * Uses the shared `Button` at its `compact` size, same as `ProfilePlayerActions`
 * (the player profile header), so the two read as one system: a filled PRIMARY
 * button for the main action, and a 32px square outlined button for the
 * icon-only ones. Replaces the bare pencil and trash glyphs that previously sat
 * inline beside the title.
 */
const EventDetailActions: React.FC<EventDetailActionsProps> = ({
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onShare,
}) => {
  // No early return on the permissions any more: sharing needs none, so a
  // read-only viewer still gets a toolbar holding just the share button.

  return (
    <div css={containerStyle}>
      {canEdit ? (
        <Button
          text={capitalizeWords('Edit Event')}
          variant='primary'
          size='compact'
          onClick={onEdit}
          icon={<EditOutlined />}
        />
      ) : null}

      {/* The shared SVG the Game page's share trigger uses, NOT antd's
          ShareAltOutlined — the two draw different glyphs and the arrow is the
          established mark. No `color`: Button injects its content color. */}
      <Button
        text={capitalizeWords('Share Event')}
        variant='outlined'
        size='compact'
        iconOnly
        onClick={onShare}
        icon={<ShareIcon size={14} />}
      />

      {canDelete ? (
        <Button
          text={capitalizeWords('Delete Event')}
          variant='outlined'
          size='compact'
          iconOnly
          onClick={onDelete}
          icon={<DeleteOutlined />}
        />
      ) : null}
    </div>
  )
}

export default EventDetailActions

// ---- styled ----
const containerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`
